import { type Ref, useRef } from 'react'
import { Dialog as BaseDialog } from '@base-ui/react/dialog'

import { confirmDialogOnEnter } from '@gto/helpers/dialog-keys'
import { cn } from '@gto/ui/cn'

/** Base UI wraps the React event, so take the handler shape from its own props. */
type PopupKeyDownHandler = NonNullable<BaseDialog.Popup.Props['onKeyDown']>

type PopupProps = BaseDialog.Popup.Props & {
  /** React 19 ref prop */
  ref?: Ref<HTMLDivElement>
  /**
   * Whether Enter presses the call to action in `Dialog.Footer`. Turn it off when
   * the content owns Enter, such as a chat composer; when a confirmation covers
   * the call to action and the covered markup cannot be made `inert`; or when
   * the action cannot be undone — a delete with no undo should answer to a
   * click or a deliberate Tab, never to a reflex press.
   * @default true
   */
  confirmOnEnter?: boolean
}

export const Popup = ({
  className,
  confirmOnEnter = true,
  initialFocus,
  onKeyDown,
  ref,
  ...props
}: PopupProps) => {
  const popupRef = useRef<HTMLDivElement>(null)

  const handleKeyDown: PopupKeyDownHandler = (event) => {
    onKeyDown?.(event)
    if (!confirmOnEnter) {
      return
    }
    // React sends the event up its own tree, so a dialog opened from inside
    // this one reaches this handler even though it renders in its own portal.
    // The helper answers only for the popup the key press came from, so a
    // dialog underneath stays quiet.
    confirmDialogOnEnter(event.nativeEvent, event.currentTarget)
  }

  return (
    <BaseDialog.Popup
      // Lets page-level layers find an open dialog, e.g. the toasts that move off its footer.
      data-ui="dialog"
      {...props}
      ref={(node) => {
        popupRef.current = node
        if (typeof ref === 'function') {
          ref(node)
        } else if (ref) {
          ref.current = node
        }
      }}
      initialFocus={initialFocus ?? popupRef}
      className={cn(
        'fixed left-1/2 top-1/2 z-modal -translate-x-1/2 -translate-y-1/2',
        'max-h-[calc(100%-32px)] max-w-[calc(100%-32px)]',
        'sm:max-h-[calc(100%-16px)] sm:max-w-[calc(100%-16px)]',
        'rounded-sm bg-paper-elevation3 shadow-8',
        'overflow-auto p-4 outline-none sm:p-2',
        // Dim parent popup when a nested/child dialog is open (Base UI suppresses child backdrops)
        'after:pointer-events-none after:absolute after:inset-0 after:z-[10] after:rounded-[inherit] after:bg-transparent after:transition-colors',
        'data-[nested-dialog-open]:after:bg-black/50',
        className,
      )}
      onKeyDown={handleKeyDown}
    />
  )
}
